
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorsList } from "@/components/ContractorsList";
import { Contractor } from "@/types/contractor";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";

const BestPlumbersPage = () => {
  const serviceType = "Plumbers";
  const pageTitle = `Best ${serviceType} in Oklahoma City, OK`;

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'plumbing'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Plumbing')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: serviceType, href: "/best-plumbers-oklahoma-city" },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": "Find top-rated, established plumbers in Oklahoma City for residential and commercial plumbing services. Free quotes, trusted professionals.",
    "url": "https://homeprosokc.com/best-plumbers-oklahoma-city",
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={pageTitle}
        description="Find top-rated, established plumbers in Oklahoma City for residential and commercial plumbing services. Free quotes, trusted professionals."
        canonicalUrl="/best-plumbers-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title={pageTitle}
          subtitle="Connect with established, reputable plumbers for all your residential and commercial plumbing needs."
          buttonText="Get a Quote"
          buttonLink="#quote-request"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Why Choose Our Plumbers</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Established Professionals</h3>
                <p>Our plumbers are well-established in the Oklahoma City area with proven experience and expertise.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Emergency Services</h3>
                <p>Many of our professionals offer 24/7 emergency plumbing services when you need help right away.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Free Estimates</h3>
                <p>Get detailed, no-obligation quotes for your plumbing projects from our network of trusted professionals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our Plumbing Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Leak detection and repair</li>
                  <li>Drain cleaning and unclogging</li>
                  <li>Water heater installation and repair</li>
                  <li>Fixture installation and replacement</li>
                  <li>Pipe repair and replacement</li>
                  <li>Sewer line services</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Commercial plumbing maintenance</li>
                  <li>Backflow prevention</li>
                  <li>Commercial fixture installation</li>
                  <li>Water line installation and repair</li>
                  <li>Commercial drain cleaning</li>
                  <li>Code compliance updates</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quote Request Form */}
        <QuoteRequestForm serviceName={pageTitle} />
        
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Plumbers in Oklahoma City</h2>
          <ContractorsList
            contractors={contractors}
            isLoading={isLoading}
            viewMode="grid"
            onViewModeChange={() => {}}
            onServiceTypeChange={() => {}}
            onLocationChange={() => {}}
            selectedServiceType=""
          />
        </div>
      </div>
    </div>
  );
};

export default BestPlumbersPage;
